import React, { useEffect, useState } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import { api } from "../lib/api";
import { Card } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Badge } from "../components/ui/badge";
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from "../components/ui/table";
import {
  ArrowLeft, Building2, MapPin, Hash, Boxes, Loader2,
} from "lucide-react";
import { statusEquipLabel, STATUS_EQUIP_BADGE, tipoClienteLabel, propriedadeLabel } from "../lib/labels";
import { AcessoPortalCard } from "../components/AcessoPortalCard";

const fmt = (n) => new Intl.NumberFormat("pt-BR").format(n || 0);

export default function ClienteDetalhe() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    api.get(`/inventario/clientes/${id}/frota`)
      .then((r) => setData(r.data))
      .catch(() => navigate("/app/inventario"))
      .finally(() => setLoading(false));
  }, [id, navigate]);

  if (loading || !data) {
    return <div className="flex justify-center py-12"><Loader2 className="w-6 h-6 animate-spin text-zinc-400" /></div>;
  }
  const c = data.cliente;

  return (
    <div className="space-y-5" data-testid="page-cliente-detalhe">
      <div className="flex items-center gap-3">
        <Button variant="ghost" size="sm" onClick={() => navigate("/app/inventario")} data-testid="back-inventario">
          <ArrowLeft className="w-4 h-4 mr-1" /> Inventário
        </Button>
        <div className="flex-1">
          <h1 className="text-xl font-bold text-zinc-900">{c.nome_fantasia}</h1>
          <div className="flex items-center gap-3 mt-1 text-xs text-zinc-500">
            <span className="font-mono">{c.cnpj || "—"}</span>
            <span>·</span>
            <span className="flex items-center gap-1"><MapPin className="w-3 h-3" />{c.cidade}/{c.uf}</span>
            <Badge className="bg-blue-50 text-blue-700 border border-blue-200 font-mono text-[10px]">
              {tipoClienteLabel(c.tipo_cliente)}
            </Badge>
          </div>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-4 gap-4">
        <Card className="p-4 lg:col-span-2">
          <h3 className="text-xs uppercase tracking-wide text-zinc-500 font-semibold mb-3">Endereço</h3>
          <div className="text-sm text-zinc-700 space-y-1">
            <div>{c.endereco} {c.numero && `, ${c.numero}`} {c.complemento && `- ${c.complemento}`}</div>
            <div className="text-zinc-500">{c.bairro} · {c.cidade}/{c.uf} · CEP {c.cep || "—"}</div>
          </div>
        </Card>
        <Card className="p-4">
          <h3 className="text-xs uppercase tracking-wide text-zinc-500 font-semibold mb-2">Frota total</h3>
          <p className="text-3xl font-bold">{fmt(data.total)}</p>
          <p className="text-xs text-zinc-400">equipamentos vinculados</p>
        </Card>
        <Card className="p-4">
          <h3 className="text-xs uppercase tracking-wide text-zinc-500 font-semibold mb-2">Por status</h3>
          <div className="space-y-1">
            {Object.entries(data.por_status || {}).map(([s, n]) => (
              <div key={s} className="flex justify-between text-xs">
                <span>{statusEquipLabel(s)}</span>
                <span className="font-mono text-zinc-500">{n}</span>
              </div>
            ))}
            {Object.keys(data.por_status || {}).length === 0 && (
              <span className="text-xs text-zinc-400">—</span>
            )}
          </div>
        </Card>
      </div>

      {/* PARTE 4 — Acesso ao portal do cliente */}
      <AcessoPortalCard clienteLocativoId={c.id} clienteNome={c.nome_fantasia} />

      <Card className="overflow-hidden" data-testid="frota-table">
        <div className="px-4 py-3 border-b border-zinc-100 flex items-center gap-2">
          <Boxes className="w-4 h-4 text-zinc-500" />
          <h3 className="text-xs uppercase tracking-wide text-zinc-700 font-semibold">Frota do cliente</h3>
        </div>
        <Table>
          <TableHeader>
            <TableRow className="bg-zinc-50">
              <TableHead className="w-16">ID</TableHead>
              <TableHead>SERIAL</TableHead>
              <TableHead className="w-24">PLACA</TableHead>
              <TableHead className="w-40">STATUS</TableHead>
              <TableHead className="w-32">PROPRIEDADE</TableHead>
              <TableHead className="w-32">MODALIDADE</TableHead>
              <TableHead className="w-28">DESDE</TableHead>
              <TableHead className="w-24"></TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {data.items.map((e) => (
              <TableRow key={e.id}>
                <TableCell className="font-mono text-xs text-zinc-500">{e.id}</TableCell>
                <TableCell className="font-mono text-xs">{e.serial}</TableCell>
                <TableCell className="font-mono text-xs">{e.placa || "—"}</TableCell>
                <TableCell>
                  <Badge className={`${STATUS_EQUIP_BADGE[e.status]} font-mono text-[10px]`}>
                    {statusEquipLabel(e.status)}
                  </Badge>
                </TableCell>
                <TableCell className="text-xs">{propriedadeLabel(e.propriedade)}</TableCell>
                <TableCell className="text-xs">{e.modalidade ? String(e.modalidade).toUpperCase() : "—"}</TableCell>
                <TableCell className="text-xs">{e.data_inicio || "—"}</TableCell>
                <TableCell>
                  <Link to={`/app/inventario/equipamento/${e.id}`}>
                    <Button size="sm" variant="ghost" data-testid={`open-equip-${e.id}`}>Abrir</Button>
                  </Link>
                </TableCell>
              </TableRow>
            ))}
            {data.items.length === 0 && (
              <TableRow><TableCell colSpan={8} className="text-center py-8 text-zinc-400">Cliente sem frota vinculada.</TableCell></TableRow>
            )}
          </TableBody>
        </Table>
      </Card>
    </div>
  );
}
